// src/views/MembersManager.jsx
import React, { useState, useEffect } from 'react';
import { client } from '../utils/api';
import DataTable from '../components/DataTable';

const CONTROLLER = 'membersController.php';

const columns = [
  { key: 'id', label: 'ID' },
  { key: 'fullName', label: 'Full Name' },
  { key: 'email', label: 'Email' },
  { key: 'city', label: 'City' },
  { key: 'membershipTo', label: 'Expires' },
];

const initialFormState = { fullName: '', email: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '', membershipFrom: '', membershipTo: '' };

export default function MembersManager() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { loadMembers(); }, []);

  const loadMembers = async () => {
    try {
      const res = await client.get(CONTROLLER);
      setMembers(res.members || []);
    } catch (err) { setError('Failed to fetch members: ' + err.message); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await client.put(CONTROLLER, editingId, form);
      } else {
        await client.post(CONTROLLER, form);
      }
      setForm(initialFormState);
      setEditingId(null);
      loadMembers();
    } catch (err) { setError(err.message); }
  };

  const handleEdit = (member) => {
    setEditingId(member.id);
    setForm(member);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;
    try {
      await client.delete(CONTROLLER, id);
      loadMembers();
    } catch (err) { setError(err.message); }
  };

  // inside the return statement of MembersManager component:
return (
 <div className="admin-view-wrapper">
  {/* 1. Header Row Area */}
  <div className="admin-view-header">
    <h2 className="admin-title">Association Members Management</h2>
    {error && <div className="admin-error">{error}</div>}
  </div>
  
  {/* 2. Form Area (Spans full available width, handles inner grid fields) */}
  <form onSubmit={handleSubmit} className="admin-form-container grid-2-col">
    <div className="form-group">
      <label>Full Name *</label>
      <input 
        type="text"
        placeholder="e.g. Alexandre Ivanov" 
        required 
        value={form.fullName} 
        onChange={e => setForm({...form, fullName: e.target.value})} 
      />
    </div>

    <div className="form-group">
      <label>Email *</label>
      <input 
        type="email" 
        placeholder="e.g. alex@example.ch" 
        required 
        value={form.email} 
        onChange={e => setForm({...form, email: e.target.value})} 
      />
    </div>

    <div className="form-group">
      <label>Phone</label>
      <input 
        type="text"
        placeholder="e.g. +41 21 000 00 00" 
        value={form.phone} 
        onChange={e => setForm({...form, phone: e.target.value})} 
      />
    </div>

    <div className="form-group">
      <label>Address Line 1</label>
      <input 
        type="text"
        placeholder="e.g. Rue de Bourg 12" 
        value={form.addressLine1} 
        onChange={e => setForm({...form, addressLine1: e.target.value})} 
      />
    </div>

    <div className="form-group">
      <label>City</label>
      <input 
        type="text"
        placeholder="e.g. Lausanne" 
        value={form.city} 
        onChange={e => setForm({...form, city: e.target.value})} 
      />
    </div>

    <div className="form-group">
      <label>State / Canton</label>
      <input 
        type="text"
        placeholder="e.g. Vaud" 
        value={form.state} 
        onChange={e => setForm({...form, state: e.target.value})} 
      />
    </div>

    <div className="form-group" style={{ gridColumn: 'span 2' }}>
      <label>Postal Code</label>
      <input 
        type="text"
        placeholder="e.g. 1003" 
        value={form.postalCode} 
        onChange={e => setForm({...form, postalCode: e.target.value})} 
      />
    </div>

    <div className="form-group">
      <label>Membership Active From</label>
      <input 
        type="date" 
        value={form.membershipFrom} 
        onChange={e => setForm({...form, membershipFrom: e.target.value})} 
      />
    </div>

    <div className="form-group">
      <label>Membership Active To</label>
      <input 
        type="date" 
        value={form.membershipTo} 
        onChange={e => setForm({...form, membershipTo: e.target.value})} 
      />
    </div>

    <div className="btn-actions-container" style={{ gridColumn: 'span 2', justifyContent: 'flex-end', marginTop: '8px' }}>
      {editingId && (
        <button 
          type="button" 
          onClick={() => { setEditingId(null); setForm(initialFormState); }} 
          className="btn btn-cancel"
        >
          Cancel
        </button>
      )}
      <button type="submit" className="btn btn-submit-members">
        {editingId ? 'Update Member' : 'Add Member'}
      </button>
    </div>
  </form>

  {/* 3. Data Entry Table (Sits cleanly beneath the form element) */}
  <div className="admin-table-section">
    <DataTable columns={columns} data={members} onEdit={handleEdit} onDelete={handleDelete} />
  </div>
</div>
);
}